import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AuthorizedButton } from '@/components/ui/AuthorizedButton'
import { usePermissions } from '@/hooks/use-permissions'
import { Loader2, Plus, Send, X } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useSubmitControl } from '@/hooks/use-control'

interface ControlTask {
  powerTarget: number
  startTime: string
  endTime: string
}

interface ControlSignalFormProps {
  poolID: string
  poolName: string
  onSuccess?: () => void
}

export default function ControlSignalForm({ poolID, poolName, onSuccess }: ControlSignalFormProps) {
  const { can } = usePermissions()
  const [tasks, setTasks] = useState<ControlTask[]>([
    {
      powerTarget: 0,
      startTime: '',
      endTime: '',
    },
  ])
  const [unit, setUnit] = useState<'W' | 'kW' | 'MW'>('W')
  const { toast } = useToast()
  const submitControl = useSubmitControl()

  const addTask = () => {
    if (tasks.length >= 96) {
      toast({
        title: 'Limit Reached',
        description: 'Maximum 96 tasks per submission',
        variant: 'destructive',
      })
      return
    }

    setTasks([
      ...tasks,
      {
        powerTarget: 0,
        startTime: '',
        endTime: '',
      },
    ])
  }

  const removeTask = (index: number) => {
    if (tasks.length === 1) {
      toast({
        title: 'Cannot Remove',
        description: 'At least one task is required',
        variant: 'destructive',
      })
      return
    }
    setTasks(tasks.filter((_, i) => i !== index))
  }

  const updateTask = (index: number, field: keyof ControlTask, value: string | number) => {
    const newTasks = [...tasks]
    newTasks[index] = {
      ...newTasks[index],
      [field]: value,
    }
    setTasks(newTasks)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i]

      if (!task.startTime || !task.endTime) {
        toast({
          title: 'Validation Error',
          description: `Task ${i + 1}: Start and end times are required`,
          variant: 'destructive',
        })
        return
      }

      const start = new Date(task.startTime)
      const end = new Date(task.endTime)

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        toast({
          title: 'Validation Error',
          description: `Task ${i + 1}: Invalid date format`,
          variant: 'destructive',
        })
        return
      }

      if (end <= start) {
        toast({
          title: 'Validation Error',
          description: `Task ${i + 1}: End time must be after start time`,
          variant: 'destructive',
        })
        return
      }
    }

    const tasksToSubmit = tasks.map((task) => ({
      powerTarget: Number(task.powerTarget),
      startTime: new Date(task.startTime).toISOString(),
      endTime: new Date(task.endTime).toISOString(),
    }))

    submitControl.mutate(
      { poolID, tasks: tasksToSubmit, unit },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: `${tasks.length} control task${tasks.length !== 1 ? 's' : ''} submitted`,
          })
          setTasks([{ powerTarget: 0, startTime: '', endTime: '' }])
          onSuccess?.()
        },
        onError: (err: any) => {
          toast({
            title: 'Error',
            description: err.message || 'Failed to submit control signals',
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Submit Control Signals</CardTitle>
        <CardDescription>
          Schedule power setpoints for {poolName} (max 96 tasks)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Power Unit Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Power Unit</label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as 'W' | 'kW' | 'MW')}
              disabled={submitControl.isPending}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="W">Watts (W)</option>
              <option value="kW">Kilowatts (kW)</option>
              <option value="MW">Megawatts (MW)</option>
            </select>
          </div>

          {/* Tasks List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">
                Control Tasks ({tasks.length}/96)
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTask}
                disabled={submitControl.isPending || tasks.length >= 96}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Task
              </Button>
            </div>

            {tasks.map((task, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Task {index + 1}</span>
                  {tasks.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTask(index)}
                      disabled={submitControl.isPending}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Power Target */}
                  <div className="space-y-1">
                    <label htmlFor={`power-${index}`} className="text-xs font-medium">
                      Power Target ({unit})
                    </label>
                    <input
                      id={`power-${index}`}
                      type="number"
                      step="any"
                      placeholder="e.g., -1000"
                      value={task.powerTarget}
                      onChange={(e) => updateTask(index, 'powerTarget', e.target.value)}
                      disabled={submitControl.isPending}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Negative = discharge/export
                    </p>
                  </div>

                  {/* Start Time */}
                  <div className="space-y-1">
                    <label htmlFor={`start-${index}`} className="text-xs font-medium">
                      Start Time
                    </label>
                    <input
                      id={`start-${index}`}
                      type="datetime-local"
                      value={task.startTime}
                      onChange={(e) => updateTask(index, 'startTime', e.target.value)}
                      disabled={submitControl.isPending}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      required
                    />
                  </div>

                  {/* End Time */}
                  <div className="space-y-1">
                    <label htmlFor={`end-${index}`} className="text-xs font-medium">
                      End Time
                    </label>
                    <input
                      id={`end-${index}`}
                      type="datetime-local"
                      value={task.endTime}
                      onChange={(e) => updateTask(index, 'endTime', e.target.value)}
                      disabled={submitControl.isPending}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <AuthorizedButton authorized={can('PoolsControl')} type="submit" disabled={submitControl.isPending} className="w-full">
            {submitControl.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Submit Control Signals
              </>
            )}
          </AuthorizedButton>
        </form>
      </CardContent>
    </Card>
  )
}
